import { DifficultyId } from '../constants';
import { getDifficulty } from './difficulty';
import { CoreBeamId, DeviceId, NodeLensId } from '../weapons/definitions';

export const enum PurchaseKind { Core = 0, Lens = 1, Device = 2, MaxLife = 3, NodeRecharge = 4, ScoreBoost = 5, Repair = 6 }

export const enum PurchaseState {
  Buy = 0,
  Equip = 1,
  Equipped = 2,
  Maxed = 3,
  InsufficientLux = 4,
  RepairNotNeeded = 5,
}

export interface PurchaseDefinition {
  readonly id: number;
  readonly kind: PurchaseKind;
  readonly target: number;
  readonly name: string;
  readonly basePrice: number;
  readonly maxPurchases: number;
}

export interface InventoryState {
  lux: number;
  core: CoreBeamId;
  lens: NodeLensId;
  device: DeviceId;
  maxLives: number;
  lives: number;
  nodeRechargeLevel: number;
  scoreBoostTicks: number;
  purchaseCounts: Uint8Array;
}

export const PURCHASES: readonly PurchaseDefinition[] = Object.freeze([
  Object.freeze({ id: 0, kind: PurchaseKind.Core, target: CoreBeamId.Pulse, name: 'CORE / PULSE', basePrice: 50, maxPurchases: 1 }),
  Object.freeze({ id: 1, kind: PurchaseKind.Core, target: CoreBeamId.Arc, name: 'CORE / ARC', basePrice: 90, maxPurchases: 1 }),
  Object.freeze({ id: 2, kind: PurchaseKind.Core, target: CoreBeamId.Prism, name: 'CORE / PRISM', basePrice: 130, maxPurchases: 1 }),
  Object.freeze({ id: 3, kind: PurchaseKind.Core, target: CoreBeamId.Lattice, name: 'CORE / LATTICE', basePrice: 150, maxPurchases: 1 }),
  Object.freeze({ id: 4, kind: PurchaseKind.Lens, target: NodeLensId.Focus, name: 'LENS / FOCUS', basePrice: 45, maxPurchases: 1 }),
  Object.freeze({ id: 5, kind: PurchaseKind.Lens, target: NodeLensId.Fan, name: 'LENS / FAN', basePrice: 85, maxPurchases: 1 }),
  Object.freeze({ id: 6, kind: PurchaseKind.Lens, target: NodeLensId.Mirror, name: 'LENS / MIRROR', basePrice: 125, maxPurchases: 1 }),
  Object.freeze({ id: 7, kind: PurchaseKind.Lens, target: NodeLensId.Tether, name: 'LENS / TETHER', basePrice: 145, maxPurchases: 1 }),
  Object.freeze({ id: 8, kind: PurchaseKind.Device, target: DeviceId.RepairField, name: 'DEVICE / REPAIR', basePrice: 70, maxPurchases: 1 }),
  Object.freeze({ id: 9, kind: PurchaseKind.Device, target: DeviceId.PhaseCut, name: 'DEVICE / PHASE CUT', basePrice: 115, maxPurchases: 1 }),
  Object.freeze({ id: 10, kind: PurchaseKind.Device, target: DeviceId.TimeFold, name: 'DEVICE / TIME FOLD', basePrice: 135, maxPurchases: 1 }),
  Object.freeze({ id: 11, kind: PurchaseKind.Device, target: DeviceId.Overdrive, name: 'DEVICE / OVERDRIVE', basePrice: 150, maxPurchases: 1 }),
  Object.freeze({ id: 12, kind: PurchaseKind.MaxLife, target: 0, name: 'HULL CAPACITY +1', basePrice: 180, maxPurchases: 2 }),
  Object.freeze({ id: 13, kind: PurchaseKind.NodeRecharge, target: 0, name: 'NODE RECALIBRATION', basePrice: 130, maxPurchases: 3 }),
  Object.freeze({ id: 14, kind: PurchaseKind.ScoreBoost, target: 0, name: 'SCORE PHASE ×2', basePrice: 80, maxPurchases: 4 }),
  Object.freeze({ id: 15, kind: PurchaseKind.Repair, target: 0, name: 'HULL REPAIR', basePrice: 60, maxPurchases: 8 }),
]);

export function isEquipmentPurchase(item: PurchaseDefinition): boolean {
  return item.kind === PurchaseKind.Core || item.kind === PurchaseKind.Lens || item.kind === PurchaseKind.Device;
}

export function isEquipped(state: InventoryState, item: PurchaseDefinition): boolean {
  if (item.kind === PurchaseKind.Core) return state.core === item.target;
  if (item.kind === PurchaseKind.Lens) return state.lens === item.target;
  if (item.kind === PurchaseKind.Device) return state.device === item.target;
  return false;
}

export function purchasePrice(item: PurchaseDefinition, difficulty: DifficultyId): number {
  return Math.ceil((item.basePrice * getDifficulty(difficulty).priceScale) / 5) * 5;
}

export function purchaseCost(state: InventoryState, item: PurchaseDefinition, difficulty: DifficultyId): number {
  return isEquipmentPurchase(item) && state.purchaseCounts[item.id] > 0 ? 0 : purchasePrice(item, difficulty);
}

export function getPurchaseState(state: InventoryState, item: PurchaseDefinition, difficulty: DifficultyId): PurchaseState {
  if (isEquipped(state, item)) return PurchaseState.Equipped;
  const count = state.purchaseCounts[item.id] ?? 0;
  if (isEquipmentPurchase(item) && count > 0) return PurchaseState.Equip;
  if (count >= item.maxPurchases) return PurchaseState.Maxed;
  if (item.kind === PurchaseKind.Repair && state.lives >= state.maxLives) return PurchaseState.RepairNotNeeded;
  if (state.lux < purchasePrice(item, difficulty)) return PurchaseState.InsufficientLux;
  return PurchaseState.Buy;
}

export function tryPurchase(state: InventoryState, itemId: number, difficulty: DifficultyId): boolean {
  const item = PURCHASES[itemId];
  if (!item) return false;
  const action = getPurchaseState(state, item, difficulty);
  if (action === PurchaseState.Equipped || action === PurchaseState.Maxed || action === PurchaseState.InsufficientLux || action === PurchaseState.RepairNotNeeded) return false;
  if (action === PurchaseState.Buy) {
    state.lux -= purchasePrice(item, difficulty);
    state.purchaseCounts[item.id] = (state.purchaseCounts[item.id] ?? 0) + 1;
  }
  switch (item.kind) {
    case PurchaseKind.Core: state.core = item.target as CoreBeamId; break;
    case PurchaseKind.Lens: state.lens = item.target as NodeLensId; break;
    case PurchaseKind.Device: state.device = item.target as DeviceId; break;
    case PurchaseKind.MaxLife: state.maxLives += 1; state.lives += 1; break;
    case PurchaseKind.NodeRecharge: state.nodeRechargeLevel += 1; break;
    case PurchaseKind.ScoreBoost: state.scoreBoostTicks += 60 * 45; break;
    case PurchaseKind.Repair: state.lives += 1; break;
  }
  return true;
}
